import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Calendar, MapPin, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getMyBookings } from "@/lib/api/bookings.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/my-bookings")({
  head: () => ({
    meta: [
      { title: "My Bookings · Surer Adda" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MyBookings,
});

function MyBookings() {
  const fetchBookings = useServerFn(getMyBookings);
  const { data, isLoading } = useQuery({ queryKey: ["my-bookings"], queryFn: () => fetchBookings() });

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Your Account</p>
            <h1 className="mt-2 text-4xl">My Bookings</h1>
          </div>
          <Button variant="outline" onClick={signOut}><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
        </div>

        <div className="mt-10 space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!isLoading && (!data || data.length === 0) && (
            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
              <p className="text-muted-foreground">You haven't made any bookings yet.</p>
              <Link to="/book"><Button className="mt-4 bg-primary text-primary-foreground">Book Now</Button></Link>
            </div>
          )}
          {data?.map((b) => (
            <div key={b.id} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{b.event_name}</h3>
                  <div className="mt-1 flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {new Date(b.event_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {b.location}</span>
                  </div>
                </div>
                <Badge variant={b.status === "confirmed" ? "default" : b.status === "cancelled" ? "destructive" : "secondary"}>
                  {b.status}
                </Badge>
              </div>
              {b.amount > 0 && <p className="mt-2 text-sm">₹{Number(b.amount).toLocaleString("en-IN")}{b.upi_txn_id ? ` · Txn ${b.upi_txn_id}` : ""}</p>}
            </div>
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
